import { CanActivateFn } from '@angular/router';
import { AccessLevel, PageArea } from '../models/extranet.model';
import { levelOf } from '../util/access-level';
import { NO_ACCESS, accessGate } from './access-gate';

const GUARDS = new Map<string, CanActivateFn>();

/**
 * The page-area gate of a route (Feature 4123), used next to the route's role guard:
 * `canActivate: [hotelManagerGuard, areaGuard('RATES')]` — see `accessGate` for the rest.
 *
 * - NONE on the area: Home with the "no access" toast. READ and EDIT pass (a READ page hides its
 *   write actions itself).
 * - `min` EDIT — a page that only creates, such as `contracts/new` (`areaGuard('CONTRACTS', 'EDIT')`):
 *   a READ member goes to the page the route sits under (`/extranet/contracts`) with the toast
 *   "Your profile can only view this page." (`profiles.readOnlyWrite`).
 * - An area that is not part of the member's group type (missing from `me.permissions`, e.g. a hotel
 *   member on an agency page): cancelled quietly — the role guard next to it redirects, as before
 *   the feature, with no toast.
 *
 * One guard per (area, level), created once: `areaGuard('RATES') === areaGuard('RATES')`, so a
 * route's `canActivate` array can be compared as a whole.
 */
export function areaGuard(area: PageArea, min: Exclude<AccessLevel, 'NONE'> = 'READ'): CanActivateFn {
  const key = `${area}:${min}`;
  let guard = GUARDS.get(key);
  if (!guard) {
    guard = accessGate((me, url) => {
      if (!me.permissions || !(area in me.permissions)) return 'skip';
      const level = levelOf(me, area);
      if (level === 'NONE') return NO_ACCESS;
      if (min === 'EDIT' && level !== 'EDIT') return { redirectTo: parentOf(url), toastKey: 'profiles.readOnlyWrite' };
      return 'admit';
    });
    GUARDS.set(key, guard);
  }
  return guard;
}

/** The page a route sits under: `/extranet/contracts/new?step=2` → `/extranet/contracts`. */
function parentOf(url: string): string {
  const path = url.split(/[?#]/)[0];
  return path.slice(0, path.lastIndexOf('/')) || '/';
}
